import MediaImage from "@/components/platform/MediaImage";
import { useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, MapPin, Search, ShieldAlert, Ticket, Navigation, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ShareButton from "@/components/platform/ShareButton";
import DirectionsButton from "@/components/platform/DirectionsButton";
import AgeGateDialog from "@/components/platform/AgeGateDialog";
import { useEventActivity, useEventClock } from "@/hooks/platform/useEventDiscovery";
import { eventCreatorCount, eventIsCurrent, eventIsLive, type EventTimeFilter } from "@/lib/eventDiscovery";
import type { EventRow } from "@/hooks/platform/useEvents";

export interface PublicEvent {
  id: string;
  title: string;
  description: string | null;
  event_date: string;
  end_date: string | null;
  venue_name: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  flyer_url: string | null;
  genres: string[] | null;
  min_age: number | null;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
  lineup: EventRow["lineup"];
}

interface Tier {
  id: string;
  event_id: string;
  name: string;
  price: number;
  currency: string;
  quantity_total: number;
  quantity_sold: number;
  is_active: boolean;
}

function usePublicEvents(limit: number) {
  return useQuery({
    queryKey: ["public-events", limit],
    refetchInterval: 30_000,
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data: events, error } = await supabase
        .from("events")
        .select(
          "id, title, description, event_date, end_date, venue_name, address, city, country, flyer_url, genres, min_age, latitude, longitude, created_at, lineup",
        )
        .eq("status", "published")
        .or(`event_date.gte.${nowIso},end_date.gte.${nowIso}`)
        .order("event_date", { ascending: true })
        .limit(limit);
      if (error) throw error;

      const ids = (events ?? []).map((e) => e.id);
      let tiers: Tier[] = [];
      if (ids.length) {
        const { data } = await supabase
          .from("ticket_types")
          .select("id, event_id, name, price, currency, quantity_total, quantity_sold, is_active")
          .in("event_id", ids)
          .eq("is_active", true);
        tiers = (data ?? []) as Tier[];
      }
      return { events: (events ?? []) as PublicEvent[], tiers };
    },
  });
}

/** Great-circle distance in km. */
function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

type SortKey = "soonest" | "recent" | "nearest";

const dateLabel = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

interface Props {
  /** "landing" trims the section for the home page, "page" is the full /events experience. */
  variant?: "landing" | "page";
  limit?: number;
}

const PublicEventsSection = ({ variant = "page", limit = 60 }: Props) => {
  const isLanding = variant === "landing";
  const { data, isLoading, error } = usePublicEvents(limit);
  const now = useEventClock();
  const { ticketEntries } = useEventActivity((data?.events ?? []).map((event) => event.id));
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("all");
  const [timeFilter, setTimeFilter] = useState<EventTimeFilter>("all");
  const cities = useMemo(() => [...new Set((data?.events ?? []).map((e) => e.city?.trim()).filter((v): v is string => !!v))].sort(), [data]);
  const [sort, setSort] = useState<SortKey>("soonest");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [radius, setRadius] = useState(50);
  const [ageGate, setAgeGate] = useState<{ id: string; title: string; minAge: number } | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedEventId = !isLanding ? searchParams.get("event") : null;

  /** Shared links land on sign-in first; the ticket page opens straight after. */
  const goToTickets = (eventId: string) =>
    navigate(`/auth?next=${encodeURIComponent(`/dashboard/groover/events?event=${eventId}`)}`);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser cannot share a location");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSort("nearest");
        setLocating(false);
      },
      () => {
        toast.error("Location permission denied");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const tiersByEvent = useMemo(() => {
    const map = new Map<string, Tier[]>();
    (data?.tiers ?? []).forEach((t) => map.set(t.event_id, [...(map.get(t.event_id) ?? []), t]));
    return map;
  }, [data]);

  const events = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = (data?.events ?? []).map((e) => ({
      ...e,
      distance:
        coords && typeof e.latitude === "number" && typeof e.longitude === "number"
          ? distanceKm(coords.lat, coords.lng, e.latitude, e.longitude)
          : null,
    }));

    if (linkedEventId) list = list.filter((e) => e.id === linkedEventId);
    if (city !== "all") list = list.filter((e) => e.city?.toLowerCase() === city.toLowerCase());
    list = list.filter((e) => timeFilter === "live" ? eventIsLive(e, now)
      : timeFilter === "current" ? eventIsCurrent(e, now)
        : new Date(e.event_date) >= now || eventIsCurrent(e, now));
    if (q) {
      list = list.filter((e) =>
        [e.title, e.venue_name, e.city, e.country, ...(e.genres ?? [])].some((v) => v?.toLowerCase().includes(q)),
      );
    }

    if (coords && sort === "nearest") {
      list = list.filter((e) => e.distance === null || e.distance <= radius);
    }

    return [...list].sort((a, b) => {
      if (sort === "recent") return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sort === "nearest" && coords) {
        return (a.distance ?? Number.POSITIVE_INFINITY) - (b.distance ?? Number.POSITIVE_INFINITY);
      }
      return new Date(a.event_date).getTime() - new Date(b.event_date).getTime();
    });
  }, [data, query, sort, coords, radius, linkedEventId, city, timeFilter, now]);

  const Heading = isLanding ? "h2" : "h1";

  return (
    <section
      id="events"
      className={`${isLanding ? "py-16 sm:py-24 bg-black text-white" : ""} ${isLanding ? "" : ""}`}
    >
      <div className={isLanding ? "container mx-auto px-4 sm:px-6" : ""}>
        <div className={`max-w-3xl ${isLanding ? "mx-auto text-center" : ""}`}>
          {isLanding && (
            <p className="text-xs sm:text-sm tracking-[0.2em] text-gray-500 uppercase font-medium mb-3">
              What&apos;s on
            </p>
          )}
          <Heading className="text-3xl sm:text-5xl font-bold tracking-tight">Upcoming Events</Heading>
          <p className="text-gray-400 mt-3 text-base sm:text-lg">
            Every published event on Metro City Groove — venue, date, time, age restrictions and tickets.
          </p>
        </div>
        {linkedEventId && <Button className="mt-5" variant="outline" onClick={() => setSearchParams({})}>See all events</Button>}

        <div className={`relative max-w-md mt-8 ${isLanding ? "mx-auto" : ""}`}>
          <Search className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by event, venue, city or genre"
            className="pl-10 bg-gray-900 border-gray-800 text-white"
          />
        </div>

        <div className={`mt-4 flex flex-wrap items-center gap-2 ${isLanding ? "justify-center" : ""}`}>
          {!isLanding && <>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger aria-label="Filter city" className="w-44 bg-gray-900 border-gray-800 text-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All cities</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={timeFilter} onValueChange={(v) => setTimeFilter(v as EventTimeFilter)}>
              <SelectTrigger aria-label="Filter event time" className="w-44 bg-gray-900 border-gray-800 text-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Upcoming</SelectItem><SelectItem value="current">Happening now</SelectItem><SelectItem value="live">Live now</SelectItem></SelectContent>
            </Select>
          </>}
          <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
            <SelectTrigger className="w-44 bg-gray-900 border-gray-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-950 border-gray-800 text-white">
              <SelectItem value="soonest">Happening soonest</SelectItem>
              <SelectItem value="recent">Recently added</SelectItem>
              <SelectItem value="nearest" disabled={!coords}>
                Closest to me
              </SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={useMyLocation} disabled={locating}>
            {locating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Navigation className="w-4 h-4 mr-1.5" />}
            {coords ? "Update my location" : "Use my location"}
          </Button>

          {coords && sort === "nearest" && (
            <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
              <SelectTrigger className="w-36 bg-gray-900 border-gray-800 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-950 border-gray-800 text-white">
                {[10, 25, 50, 100, 250].map((r) => (
                  <SelectItem key={r} value={String(r)}>
                    Within {r} km
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {isLoading ? (
          <p className={`text-gray-400 mt-10 ${isLanding ? "text-center" : ""}`}>Loading events…</p>
        ) : error ? (
          <p className={`text-red-400 mt-10 ${isLanding ? "text-center" : ""}`}>{(error as Error).message}</p>
        ) : events.length === 0 ? (
          <div className="mt-12 border border-white/10 rounded-xl p-10 text-center max-w-2xl mx-auto">
            <Calendar className="w-8 h-8 mx-auto text-gray-500" />
            <p className="mt-3 text-gray-300 font-medium">{linkedEventId ? "This event is not currently available for tickets" : "No published events right now"}</p>
            <p className="text-sm text-gray-500 mt-1">{query || city !== "all" || timeFilter !== "all" ? "Try another city or time filter." : "Venues publish events from their dashboard — check back soon."}</p>
            {isLanding && (
              <Button asChild className="mt-5 bg-white text-black hover:bg-gray-200">
                <Link to="/auth">Create a free account</Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 mt-10">
            {events.map((e) => {
              const tiers = tiersByEvent.get(e.id) ?? [];
              const from = tiers.length ? Math.min(...tiers.map((t) => Number(t.price))) : null;
              const soldOut = tiers.length > 0 && tiers.every((t) => t.quantity_sold >= t.quantity_total);
              return (
                <Card key={e.id} className="bg-gray-950 border-gray-800 overflow-hidden flex flex-col">
                  <div className="h-40 bg-gray-900">
                    {e.flyer_url ? (
                      <MediaImage
                        src={e.flyer_url}
                        alt={`${e.title} event flyer`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-700">
                        <Ticket className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-5 space-y-3 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-lg leading-tight text-white">{e.title}</h3>
                      {eventIsLive(e, now) && <Badge variant="destructive">Live now</Badge>}
                      {typeof e.min_age === "number" && e.min_age > 0 && (
                        <Badge className="bg-red-500/15 text-red-300 border-0 shrink-0">
                          <ShieldAlert className="w-3 h-3 mr-1" />
                          {e.min_age}+
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-1.5 text-sm text-gray-400">
                      <p className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 shrink-0" />
                        {dateLabel(e.event_date)}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock className="w-4 h-4 shrink-0" />
                        {timeLabel(e.event_date)}
                        {e.end_date ? ` – ${timeLabel(e.end_date)}` : ""}
                      </p>
                      <p className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {[e.venue_name, e.address || e.city, e.country].filter(Boolean).join(" • ") ||
                            "Venue to be announced"}
                        </span>
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 flex flex-wrap gap-x-3"><span>{eventCreatorCount(e.lineup)} lineup creators</span><span>{ticketEntries[e.id] ?? 0} ticket places booked</span></p>

                    {e.genres?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {e.genres.slice(0, 4).map((g) => (
                          <Badge key={g} variant="outline" className="border-gray-700 text-gray-400 text-[10px]">
                            {g}
                          </Badge>
                        ))}
                      </div>
                    ) : null}

                    {typeof e.distance === "number" && (
                      <p className="text-xs text-emerald-300 flex items-center gap-1.5">
                        <Navigation className="w-3.5 h-3.5" />
                        {e.distance < 1 ? "Less than 1" : Math.round(e.distance)} km away
                      </p>
                    )}

                    <div className="mt-auto pt-3 border-t border-gray-800 flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-300">
                        {tiers.length === 0
                          ? "Tickets soon"
                          : soldOut
                            ? "Sold out"
                            : from === 0
                              ? "Free entry"
                              : `From R${from}`}
                      </span>
                      <div className="flex items-center gap-2">
                        <DirectionsButton
                          name={e.venue_name || e.title}
                          lat={e.latitude}
                          lng={e.longitude}
                          address={[e.address, e.city, e.country].filter(Boolean).join(", ") || null}
                          size="icon"
                          variant="outline"
                        />
                        <ShareButton path={`/events?event=${e.id}`} title={e.title} size="icon" variant="outline" />
                        <Button
                          size="sm"
                          disabled={soldOut}
                          className="bg-white text-black hover:bg-gray-200 disabled:opacity-50"
                          onClick={() => {
                            const restricted = typeof e.min_age === "number" && e.min_age > 0;
                            if (restricted) {
                              setAgeGate({ id: e.id, title: e.title, minAge: e.min_age as number });
                              return;
                            }
                            goToTickets(e.id);
                          }}
                        >
                          <Ticket className="w-4 h-4 mr-1" />
                          {soldOut ? "Sold out" : "Get tickets"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {isLanding && events.length > 0 && (
          <div className="mt-10 text-center">
            <Button asChild variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10">
              <Link to="/events">See all events</Link>
            </Button>
          </div>
        )}
      </div>

      <AgeGateDialog
        open={!!ageGate}
        onOpenChange={(o) => !o && setAgeGate(null)}
        minAge={ageGate?.minAge ?? 18}
        eventTitle={ageGate?.title ?? "This event"}
        onConfirmed={() => ageGate && goToTickets(ageGate.id)}
      />
    </section>
  );
};

export default PublicEventsSection;
